/**
 * React binding for the active dataset.
 *
 * The store itself lives in `data/dataset.ts` and is deliberately not React
 * state — the request thunks in `DataHubConsoleSlice` read it to set the
 * request header and cannot subscribe to a context. This hook is the component-side view of the same
 * value, so the pill and the API layer can never disagree about which dataset
 * is active.
 */

import { useCallback, useEffect, useState } from "react";

import {
  DatasetKind,
  getActiveDataset,
  setActiveDataset,
  subscribeToDataset,
} from "../data/dataset";

export function useActiveDataset(): [DatasetKind, (next: DatasetKind) => void] {
  const [dataset, setDataset] = useState<DatasetKind>(getActiveDataset);

  useEffect(() => {
    // Re-read on mount as well as subscribing: another component may have
    // changed it between this component's first render and its effect.
    setDataset(getActiveDataset());
    return subscribeToDataset(setDataset);
  }, []);

  const select = useCallback((next: DatasetKind) => {
    setActiveDataset(next);
  }, []);

  return [dataset, select];
}
